/**
 * @file index.tsx
 *
 * @fileoverview Renders the SEO Search Content footer text. When the field uses the "script" text format the
 * embedded <script> tags are inserted client side so that they are actually executed. Scripts inside
 * dangerouslySetInnerHTML are never run by the browser and would be stripped by the sanitizer anyway, so the
 * markup and the scripts have to be handled separately.
 */
import React from 'react';
import { useClientScriptInsert } from '~/jobiqo/hooks/useClientScriptInsert';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';

interface SEOSearchFooterProps {
  /**
   * The processed footer content coming from the SEO Search Content entity.
   */
  content: string;
  /**
   * The text format the footer field is using. Only "script" is allowed to execute scripts.
   */
  format?: string | null;
  /**
   * A unique identifier for the search page rendering the footer. Used to key the inserted scripts.
   */
  id: string;
}

export default function SEOSearchFooter({
  content,
  format,
  id,
}: SEOSearchFooterProps) {
  const isScriptFormat = format === 'script';
  const { strippedJsString } = useClientScriptInsert({
    content: isScriptFormat ? content : '',
    id,
    identifier: 'seo-search-content',
  });

  return (
    <WYSIWYGText
      format="full_html"
      content={isScriptFormat ? strippedJsString ?? '' : content}
    />
  );
}
